import React from 'react';
import {
  Grid,
  Card,
  Avatar,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import {
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getUserAvatarSrc } from '../../../utils/avatarUtils';

const UserCards = ({
  users,
  currentUser,
  onToggleActivity,
}) => {
  const { t } = useTranslation(['admin', 'common']);

  const renderRoleChips = (user) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {Array.isArray(user.roles) ? user.roles.map((role) => (
          <Chip 
            key={role} 
            label={role} 
            size="small"
            color={role === 'admin' ? 'error' : role === 'teacher' ? 'warning' : 'default'}
          />
        )) : (
          <Typography variant="body2" color="text.secondary">
            {t('admin:users.no_roles')}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Grid container spacing={3}>
      {users.map((user) => (
        <Grid item xs={12} sm={6} md={4} key={user.id}>
          <Card sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <Avatar
                alt={user.fullName}
                src={getUserAvatarSrc(user)}
                sx={{ width: 56, height: 56 }}
              >
                {user.fullName.charAt(0)}
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}
                >
                  {user.fullName}
                  {currentUser?.email === user.email && (
                    <Chip
                      label={t('common:you')}
                      size="small"
                      color="primary"
                      sx={{
                        ml: 1,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    />
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {user.email}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {t('admin:users.role')}:
                  </Typography>
                  {renderRoleChips(user)}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {t('admin:users.status')}:
                  </Typography>
                  <Chip
                    label={!user.isActive ? t('admin:users.status_banned') : t('admin:users.status_active')}
                    size="small"
                    color={!user.isActive ? 'error' : 'success'}
                    sx={{ height: 24 }}
                  />
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {t('admin:users.registration_date')}:{' '}
                  {new Date(user.metadata.createdAt).toLocaleDateString()}  
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                mt: 'auto',
              }}
            >
              <Tooltip
                title={
                  user.isActive
                    ? t('admin:users.deactivate')
                    : t('admin:users.activate')
                }
              >
                <IconButton
                  onClick={() =>
                    onToggleActivity(user.id, user.isActive)
                  }
                  color={!user.isActive ? 'error' : 'success'}
                  size="small"
                >
                  {!user.isActive ? (
                    <ToggleOff fontSize="small" />
                  ) : (
                    <ToggleOn fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default UserCards; 